<!--
  @component ConsumerShell — the tenant-face chrome (Slate Ink).
  tier:      layout chrome (consumer face only — admin uses Shell)
  shape:     full-width frosted header (brand + project selector +
             account) · left network-product rail (Deployments live,
             the rest "Soon") · contextual sub-nav inside a project ·
             main outlet.

  Layout (signed in):
    ┌───────────────────────────────────────────────┐
    │ HEADER  Liquidefy·Network …  project · account │ 56px, frosted
    ├──────────┬────────────────────────────────────┤
    │ RAIL     │ sub-nav (project sections)          │
    │ Deploy●  ├────────────────────────────────────┤
    │ Coming   │ main outlet (active screen)         │
    │ soon …   │                                     │
    └──────────┴────────────────────────────────────┘

  Two navigation tiers, by design:
    - RAIL (left) — TOP-LEVEL network products. The consumer app is one
      network product ("Deployments"); as more network concerns get
      faces they each become a rail entry. Today only Deployments is
      live; the rest of `liquidefy/network/` is listed "Soon".
    - HEADER + SUB-NAV — the chrome WITHIN the active product: the
      project selector + account menu (header) and the per-project
      section tabs (sub-nav, only inside a project).

  Signed out: header (brand only) + centered main; no rail/sub-nav.
-->
<script lang="ts" module>
  import { getContext } from "svelte";

  export const CONSUMER_SHELL_KEY: unique symbol =
    Symbol("consumer.shell");

  export interface ConsumerShellContext {
    /** True iff a project is currently active. */
    readonly inProject: boolean;
  }

  export function useConsumerShell(): ConsumerShellContext {
    const ctx = getContext<ConsumerShellContext | undefined>(
      CONSUMER_SHELL_KEY,
    );
    if (!ctx) {
      throw new Error(
        "useConsumerShell() called outside of a ConsumerShell tree.",
      );
    }
    return ctx;
  }

  /** A top-level network product in the rail. `available: false`
   *  renders grayed with a "Soon" badge and is non-navigable. */
  interface NetworkConcern {
    readonly id: string;
    readonly label: string;
    readonly available: boolean;
  }

  /**
   * The rail's top-level network products. Mirrors `liquidefy/network/`:
   * Deployments is the one built + live product; every other network
   * concern is listed "Soon" until it gets its own face. Curate freely
   * (flip `available` when a face ships). `docs/` + `servers/` omitted.
   */
  const NETWORK_CONCERNS: readonly NetworkConcern[] = [
    { id: "projects",    label: "Projects",    available: true },
    { id: "deployments", label: "Deployments", available: true },
    // ── Soon — the rest of liquidefy/network/ ──────────────────────
    { id: "security",    label: "Security",    available: false },
    { id: "connections", label: "Connections", available: false },
    { id: "routers",     label: "Routers",     available: false },
    { id: "ops",         label: "Ops",         available: false },
    { id: "signals",     label: "Signals",     available: false },
    { id: "llm",         label: "LLM",         available: false },
  ];

  /** 24×24 stroke icons (currentColor), keyed by concern id. Static
   *  authored markup — safe to render with {@html} inside the <svg>. */
  const CONCERN_ICONS: Record<string, string> = {
    projects:
      '<rect width="7" height="7" x="3" y="3" rx="1"/><rect width="7" height="7" x="14" y="3" rx="1"/><rect width="7" height="7" x="14" y="14" rx="1"/><rect width="7" height="7" x="3" y="14" rx="1"/>',
    deployments:
      '<path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>',
    security:
      '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>',
    connections:
      '<circle cx="6" cy="6" r="3"/><circle cx="18" cy="18" r="3"/><path d="M6 9v3a3 3 0 0 0 3 3h3M15 6h3a3 3 0 0 1 3 3v0"/>',
    routers:
      '<rect width="20" height="8" x="2" y="14" rx="2"/><path d="M6.01 18H6M10.01 18H10M15 10V6M9.5 6 15 2l5.5 4"/>',
    ops:
      '<path d="M12 2v2M12 20v2m10-10h-2M4 12H2m15.07-5.07-1.41 1.41M8.34 15.66l-1.41 1.41m0-10.14 1.41 1.41m7.32 7.32 1.41 1.41"/><circle cx="12" cy="12" r="4"/>',
    signals:
      '<path d="M2 12h3l3-9 4 18 3-9h7"/>',
    llm:
      '<path d="M12 5a3 3 0 0 0-6 0v1a3 3 0 0 0-1 5.83A3 3 0 0 0 8 18a3 3 0 0 0 4 1 3 3 0 0 0 4-1 3 3 0 0 0 1-6.17A3 3 0 0 0 18 6V5a3 3 0 0 0-6 0Z"/>',
  };

  const LIVE_CONCERNS = NETWORK_CONCERNS.filter((c) => c.available);
  const SOON_CONCERNS = NETWORK_CONCERNS.filter((c) => !c.available);
</script>

<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useIdentityIO } from "../../consumer/src/accounts/IdentityIO.svelte";
  import type { FeatureHeaderRegistry } from "./primitives/feature-header.js";
  import DeploymentsHeader from "../../consumer/src/features/deployments/DeploymentsHeader.svelte";

  interface Props {
    currentProjectId: string | null;
    currentProjectScreen: string | null;
    currentPath: string;
    onNavigate: (path: string) => void;
    children?: Snippet;
  }
  const {
    currentProjectId,
    currentProjectScreen,
    currentPath,
    onNavigate,
    children,
  }: Props = $props();

  const identity = useIdentityIO();

  // Feature-header registry: feature id → its sub-header component. The
  // shell owns the SLOT + the lookup; each feature owns its header content
  // (its tabs, title, actions). Add a concern here as its face ships —
  // mirrors the rail's products.
  const FEATURE_HEADERS: FeatureHeaderRegistry = {
    deployments: DeploymentsHeader,
  };

  // Which rail item is highlighted: Deployments owns the whole /deployments
  // route space; everything else (the /projects home, account pages) is
  // Projects. Pure highlight; navigation is goConcern's job.
  const activeConcern = $derived(
    currentPath.startsWith("/deployments") ? "deployments" : "projects",
  );

  const inProject = $derived(currentProjectId !== null);
  const currentProject = $derived(
    identity.projects.find((p) => p.id === currentProjectId) ?? null,
  );
  const initials = $derived(
    (identity.user?.displayName ?? identity.user?.email ?? "?")
      .trim().charAt(0).toUpperCase(),
  );

  // The Deployments sub-header shows across the whole /deployments route space.
  // Its active tab is the current section (ConsumerRoot derives it from the
  // path); the scope selector inside it owns "which projects".
  const currentFeature = $derived(
    currentPath.startsWith("/deployments") ? "deployments" : null,
  );
  const ActiveHeader = $derived(
    currentFeature ? FEATURE_HEADERS[currentFeature] : undefined,
  );

  setContext(CONSUMER_SHELL_KEY, {
    get inProject() { return inProject; },
  } satisfies ConsumerShellContext);

  let accountMenuOpen = $state(false);
  let projectMenuOpen = $state(false);

  function goProject(projectId: string, screenId: string): void {
    onNavigate(`/projects/${projectId}/${screenId}`);
    projectMenuOpen = false;
  }
  function goConcern(concern: NetworkConcern): void {
    if (!concern.available) return;
    if (!identity.user) { onNavigate("/"); return; }
    if (concern.id === "projects") onNavigate("/projects");
    else if (concern.id === "deployments") onNavigate("/deployments");
  }
</script>

<div class="shell" class:signed-out={!identity.user}>
  <!-- ── Header (full width, frosted) ─────────────────────────── -->
  <header class="topbar">
    <button
      type="button"
      class="brand"
      onclick={() => onNavigate(identity.user ? "/projects" : "/")}
    >
      <span class="brand-mark" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
          <path d="M4 14c4-1 6-4 8-9 2 5 4 8 8 9-4 1-6 4-8 9-2-5-4-8-8-9Z" />
        </svg>
      </span>
      <span class="brand-text">
        <span class="brand-name">Liquidefy</span>
        <span class="brand-sub">Network</span>
      </span>
    </button>

    {#if identity.user}
      <div class="topbar-right">
        <!-- Project selector -->
        <div class="selector">
          <button
            type="button"
            class="selector-btn"
            onclick={() => (projectMenuOpen = !projectMenuOpen)}
            aria-haspopup="menu"
            aria-expanded={projectMenuOpen}
          >
            {currentProject ? currentProject.name : "All projects"}
            <span class="caret" aria-hidden="true">▾</span>
          </button>
          {#if projectMenuOpen}
            <div class="menu" role="menu">
              <button
                type="button"
                class="menu-item"
                onclick={() => { onNavigate("/projects"); projectMenuOpen = false; }}
                role="menuitem"
              >
                All projects
              </button>
              {#if identity.projects.length > 0}
                <div class="menu-sep"></div>
                {#each identity.projects as p (p.id)}
                  <button
                    type="button"
                    class="menu-item"
                    class:active={p.id === currentProjectId}
                    onclick={() => goProject(p.id, "overview")}
                    role="menuitem"
                  >
                    {p.name}
                  </button>
                {/each}
              {/if}
            </div>
          {/if}
        </div>

        <!-- Account menu -->
        <div class="selector right">
          <button
            type="button"
            class="account-btn"
            onclick={() => (accountMenuOpen = !accountMenuOpen)}
            aria-haspopup="menu"
            aria-expanded={accountMenuOpen}
          >
            <span class="avatar" aria-hidden="true">{initials}</span>
            <span class="account-name">{identity.user.displayName}</span>
            <span class="caret" aria-hidden="true">▾</span>
          </button>
          {#if accountMenuOpen}
            <div class="menu right-aligned" role="menu">
              <button type="button" class="menu-item" onclick={() => { onNavigate("/account"); accountMenuOpen = false; }} role="menuitem">
                Account
              </button>
              <button type="button" class="menu-item" onclick={() => { onNavigate("/billing"); accountMenuOpen = false; }} role="menuitem">
                Billing
              </button>
              <div class="menu-sep"></div>
              <button type="button" class="menu-item destructive" onclick={() => { void identity.signOut(); accountMenuOpen = false; onNavigate("/"); }} role="menuitem">
                Sign out
              </button>
            </div>
          {/if}
        </div>
      </div>
    {/if}
  </header>

  {#if identity.user}
    <!-- ── Network-product rail ───────────────────────────────── -->
    <aside class="sidebar">
      <nav class="rail-nav" aria-label="Network products">
        {#each LIVE_CONCERNS as c (c.id)}
          <button
            type="button"
            class="concern"
            class:active={c.id === activeConcern}
            aria-current={c.id === activeConcern ? "page" : undefined}
            onclick={() => goConcern(c)}
          >
            <span class="concern-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                {@html CONCERN_ICONS[c.id] ?? ""}
              </svg>
            </span>
            <span class="concern-label">{c.label}</span>
          </button>
        {/each}
      </nav>

      <p class="rail-eyebrow">Coming soon</p>
      <nav class="rail-nav" aria-label="Coming soon">
        {#each SOON_CONCERNS as c (c.id)}
          <div class="concern soon" aria-disabled="true">
            <span class="concern-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                {@html CONCERN_ICONS[c.id] ?? ""}
              </svg>
            </span>
            <span class="concern-label">{c.label}</span>
            <span class="soon-pill">Soon</span>
          </div>
        {/each}
      </nav>

      <div class="rail-foot">
        <p class="rail-foot-text">More of the network is coming online.</p>
      </div>
    </aside>

    <!-- ── Content column (sub-nav + outlet) ──────────────────── -->
    <div class="content">
      {#if ActiveHeader}
        <ActiveHeader
          activeScreen={currentProjectScreen}
          {onNavigate}
        />
      {/if}
      <main class="main">
        {@render children?.()}
      </main>
    </div>
  {:else}
    <!-- Signed out: centered content, no rail. -->
    <main class="main main-centered">
      {@render children?.()}
    </main>
  {/if}
</div>

<style>
  .shell {
    display: grid;
    grid-template-columns: 240px 1fr;
    grid-template-rows: var(--header-h) 1fr;
    height: 100vh;
    width: 100vw;
    overflow: hidden;
    background: var(--surface-base);
    color: var(--text-primary);
    font-family: var(--type-font);
    letter-spacing: var(--type-tracking);
  }
  .shell.signed-out { grid-template-columns: 1fr; }

  /* ── Header ─────────────────────────────────────────────────── */
  .topbar {
    grid-column: 1 / -1;
    grid-row: 1;
    display: flex;
    align-items: center;
    gap: var(--space-4);
    padding: 0 var(--space-5);
    background: rgba(255, 255, 255, 0.025);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    border-bottom: 0.5px solid var(--border);
    box-shadow: var(--shadow-header);
    z-index: 10;
  }
  :global([data-theme="light"]) .topbar { background: var(--surface-overlay); }

  .brand {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    background: transparent;
    border: 0;
    cursor: pointer;
    padding: var(--space-1) 0;
    width: calc(240px - var(--space-5));
    flex-shrink: 0;
  }
  .brand-mark {
    display: grid;
    place-items: center;
    width: 24px;
    height: 24px;
    color: var(--text-primary);
    opacity: 0.9;
  }
  .brand-mark svg { width: 20px; height: 20px; }
  .brand-text { display: inline-flex; align-items: baseline; gap: 6px; line-height: 1; }
  .brand-name { font-size: 15.5px; font-weight: 600; letter-spacing: -0.02em; color: var(--text-primary); }
  .brand-sub  { font-size: 15.5px; font-weight: 400; letter-spacing: -0.01em; color: var(--text-tertiary); transition: color var(--ease-fast); }
  .brand:hover .brand-sub { color: var(--text-secondary); }

  .topbar-right { margin-left: auto; display: flex; align-items: center; gap: var(--space-2); }
  .selector { position: relative; }
  .selector-btn {
    background: transparent;
    border: 0.5px solid var(--border);
    border-radius: var(--radius-md);
    color: var(--text-secondary);
    font: inherit;
    font-size: 13px;
    padding: 7px var(--space-3);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    transition: background var(--ease-fast), color var(--ease-fast), border-color var(--ease-fast);
  }
  .selector-btn:hover { background: var(--bg-hover); color: var(--text-primary); border-color: var(--border-strong); }
  .account-btn {
    background: transparent;
    border: 0.5px solid transparent;
    border-radius: var(--radius-md);
    color: var(--text-secondary);
    font: inherit;
    font-size: 13px;
    padding: 4px var(--space-2) 4px 4px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    transition: background var(--ease-fast), color var(--ease-fast);
  }
  .account-btn:hover { background: var(--bg-hover); color: var(--text-primary); }
  .avatar {
    width: 26px; height: 26px;
    border-radius: var(--radius-pill);
    display: grid; place-items: center;
    font-size: 11px; font-weight: 600;
    color: rgba(255, 255, 255, 0.78);
    background: linear-gradient(180deg, rgba(42,42,52,0.9) 0%, rgba(28,28,36,0.86) 100%);
    border: 0.5px solid rgba(255, 255, 255, 0.1);
  }
  .account-name { max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .caret { font-size: 9px; opacity: 0.5; }

  .menu {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    min-width: 220px;
    background: var(--surface-raised);
    border: 0.5px solid var(--border);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-lg);
    padding: var(--space-1);
    z-index: 100;
    display: flex;
    flex-direction: column;
  }
  .menu.right-aligned { left: auto; right: 0; }
  .menu-item {
    background: transparent;
    border: 0;
    color: var(--text-secondary);
    text-align: left;
    padding: 8px var(--space-3);
    font: inherit;
    font-size: 13px;
    cursor: pointer;
    border-radius: var(--radius-sm);
    transition: background var(--ease-fast), color var(--ease-fast);
  }
  .menu-item:hover { background: var(--bg-hover); color: var(--text-primary); }
  .menu-item.active { background: var(--bg-selected); color: var(--text-primary); }
  .menu-item.destructive { color: var(--error); }
  .menu-sep { height: 0.5px; background: var(--border); margin: var(--space-1) var(--space-2); }

  /* ── Rail ───────────────────────────────────────────────────── */
  .sidebar {
    grid-column: 1;
    grid-row: 2;
    background: var(--surface-sunken);
    border-right: 0.5px solid var(--border);
    padding: var(--space-4) var(--space-3);
    display: flex;
    flex-direction: column;
    gap: 2px;
    overflow-y: auto;
  }
  .rail-nav { display: flex; flex-direction: column; gap: 2px; }
  .concern {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 38px;
    padding: 0 var(--space-2);
    border: 0;
    border-radius: var(--radius-md);
    background: transparent;
    color: var(--text-secondary);
    font: inherit;
    font-size: 13.5px;
    cursor: pointer;
    text-align: left;
    width: 100%;
    transition: background var(--ease-fast), color var(--ease-fast);
  }
  .concern:hover:not(.soon) { background: var(--bg-hover); color: var(--text-primary); }
  .concern.active { background: var(--bg-active); color: var(--text-primary); }
  .concern.soon { cursor: default; color: var(--text-ghost); }
  .concern-icon { display: grid; place-items: center; width: 18px; height: 18px; flex-shrink: 0; color: currentColor; }
  .concern-icon svg { width: 17px; height: 17px; }
  .concern.active .concern-icon { color: var(--text-primary); }
  .concern-label { flex: 1; }
  .rail-eyebrow {
    font-size: 10.5px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--text-ghost);
    padding: var(--space-4) var(--space-2) var(--space-2);
  }
  .soon-pill {
    font-size: 9.5px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--text-tertiary);
    background: var(--bg-hover);
    border: 0.5px solid var(--border-subtle);
    border-radius: var(--radius-pill);
    padding: 2px 7px;
  }
  .rail-foot { margin-top: auto; padding: var(--space-4) var(--space-2) 0; }
  .rail-foot-text { font-size: 11px; line-height: 1.5; color: var(--text-ghost); }

  /* ── Content ────────────────────────────────────────────────── */
  .content {
    grid-column: 2;
    grid-row: 2;
    display: flex;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
  }
  .main {
    overflow: auto;
    padding: var(--space-8);
    scrollbar-width: thin;
    scrollbar-color: var(--border) transparent;
  }
  .main-centered {
    grid-column: 1;
    grid-row: 2;
    display: flex;
    align-items: center;
    justify-content: center;
  }
</style>
